import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
/** import third-party libraries in the section above, keep the ascending order */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, keep the ascending order */

import Spacer from 'widgets-v5/spacer';
/** import widgets components in the section above, keep the ascending order */

import { getCurrencySignforCurrency } from 'utils/formatting';
/** import classes and others in the section above, keep the ascending order */

const TierCalculationStatus = ({ tierCalculation, name, showTitle = true, isInternalUser }) => {
    const renderTierCalculationStatus = () => {
        if (!tierCalculation || !tierCalculation.calculatedAt) {
            return (
                <Typography variant="body2" color="textSecondary">
                    No tier calculation data available for {name}
                </Typography>
            );
        }

        const {
            cpmBasedSpend,
            calculatedAt,
            dateRange,
            currency,
            publicInventoryTechFee,
            privateInventoryTechFee,
            timezone,
        } = tierCalculation;

        if (timezone !== 'UTC') {
            return (
                <Typography variant="body2">
                    Unable to display tier calculation status for {name} due to timezone mismatch.
                </Typography>
            );
        }

        const dateFormat = 'MMMM Do, YYYY hh:mm A Z';

        const calculatedDate = moment(calculatedAt)
            .utc()
            .format(dateFormat);
        const startDate = dateRange?.start
            ? moment(dateRange.start)
                  .utc()
                  .format(dateFormat)
            : 'N/A';
        const endDate = dateRange?.end
            ? moment(dateRange.end)
                  .utc()
                  .format(dateFormat)
            : 'N/A';
        const formattedSpend =
            cpmBasedSpend !== null && cpmBasedSpend !== undefined
                ? numeral(cpmBasedSpend).format('0,0.00')
                : '0.00';
        const currencySymbol = getCurrencySignforCurrency(currency) || '$';
        const publicFee =
            publicInventoryTechFee !== null && publicInventoryTechFee !== undefined
                ? numeral(publicInventoryTechFee * 100).format('0.00')
                : '0.00';
        const privateFee =
            privateInventoryTechFee !== null && privateInventoryTechFee !== undefined
                ? numeral(privateInventoryTechFee * 100).format('0.00')
                : '0.00';

        // Self serve users don't see the calculated amount in the UI
        // Because this amount includes overdeliveries and could create confusion
        // We show the calculated amount in the UI for internal users only
        if (!isInternalUser) {
            return (
                <Typography variant="body2">
                    Updated on {calculatedDate}
                    <br />
                    Calculated<span style={{ color: '#666' }}>¹</span> from {startDate} to {endDate}
                    <br />
                    Public inventory platform fee<span style={{ color: '#666' }}>²</span>:{' '}
                    {publicFee}%
                    <br />
                    Private deals platform fee<span style={{ color: '#666' }}>²</span>: {privateFee}
                    %
                    <Spacer type="small" />
                    <span style={{ color: '#666' }}>
                        ¹ Calculated in {timezone || 'UTC'} timezone
                        <br />² Platform fee updates are guaranteed once every 24 hours
                    </span>
                </Typography>
            );
        }

        return (
            <Typography variant="body2">
                On {calculatedDate}, this organization had spent {currencySymbol}
                {formattedSpend} {currency}
                <br />
                Calculated<span style={{ color: '#666' }}>¹</span> from {startDate} to {endDate}
                <br />
                Public inventory platform fee<span style={{ color: '#666' }}>²</span>: {publicFee}%
                <br />
                Private deals platform fee<span style={{ color: '#666' }}>²</span>: {privateFee}%
                <Spacer type="small" />
                <span style={{ color: '#666' }}>
                    ¹ Calculated in {timezone || 'UTC'} timezone
                    <br />
                    ² Platform fee updates are guaranteed once every 24 hours
                    <br />* This spend includes overdeliveries
                </span>
            </Typography>
        );
    };

    return (
        <Box mt={2}>
            {showTitle && (
                <Typography gutterBottom variant="h6">
                    Current Tier Calculation Status
                </Typography>
            )}
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Latest tier calculation information for {name}
            </Typography>
            <Box mt={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                {renderTierCalculationStatus()}
            </Box>
        </Box>
    );
};

export default TierCalculationStatus;
